import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AuditChip } from '@/components/audit/AuditChip';
import { RunPayoutsDialog } from '@/features/commerce-ops/RunPayoutsDialog';
import { payoutStateTone } from '@/features/commerce-ops/orderState';
import { usePayouts } from '@/queries/commerceOps';
import { useApiError } from '@/lib/useApiError';
import { formatCents } from '@/lib/money';
import { useUiStore } from '@/stores/ui';
import type { Payout } from '@/schemas/admin/commerce/payouts';

export const Route = createFileRoute('/_authed/commerce/payouts')({
  component: PayoutsScreen,
});

function PayoutsScreen() {
  const { t } = useTranslation('commerce');
  const list = usePayouts();
  const resolveError = useApiError();
  const locale = useUiStore((s) => s.locale);

  const [running, setRunning] = React.useState(false);

  const columns: readonly DataColumn<Payout>[] = [
    {
      id: 'designer',
      header: t('payouts.table.designer'),
      cell: (p) => p.designer_name ?? p.designer_id,
      sortBy: (p) => p.designer_name ?? '',
    },
    {
      id: 'period',
      header: t('payouts.table.period'),
      cell: (p) => (
        <span dir="ltr" className="font-mono text-xs">
          {p.period_start} → {p.period_end}
        </span>
      ),
      sortBy: (p) => p.period_start,
    },
    {
      id: 'gross',
      header: t('payouts.table.gross'),
      align: 'end',
      cell: (p) => (
        <span dir="ltr" className="font-mono">
          {formatCents(p.gross_cents, locale, p.currency)}
        </span>
      ),
      sortBy: (p) => p.gross_cents,
    },
    {
      id: 'fees',
      header: t('payouts.table.fees'),
      align: 'end',
      cell: (p) => (
        <span dir="ltr" className="font-mono text-[color:var(--color-fg-muted)]">
          {formatCents(p.fees_cents, locale, p.currency)}
        </span>
      ),
    },
    {
      id: 'net',
      header: t('payouts.table.net'),
      align: 'end',
      cell: (p) => (
        <span dir="ltr" className="font-mono font-semibold">
          {formatCents(p.net_cents, locale, p.currency)}
        </span>
      ),
      sortBy: (p) => p.net_cents,
    },
    {
      id: 'state',
      header: t('payouts.table.state'),
      cell: (p) => <Badge tone={payoutStateTone(p.state)}>{t(`payouts.states.${p.state}`)}</Badge>,
      sortBy: (p) => p.state,
    },
    {
      id: 'createdAt',
      header: t('payouts.table.createdAt'),
      cell: (p) => <DateTime value={p.created_at ?? null} />,
      sortBy: (p) => p.created_at ?? '',
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('payouts.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('payouts.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AuditChip />
          <Button onClick={() => setRunning(true)}>
            <Play aria-hidden className="size-4" />
            {t('payouts.run.trigger')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <DataTable<Payout>
            data={list.data ?? []}
            columns={columns}
            rowKey={(p) => p.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{
              title: t('payouts.empty.title'),
              body: t('payouts.empty.body'),
            }}
            initialSort={{ id: 'createdAt', direction: 'desc' }}
          />
        </CardContent>
      </Card>

      <RunPayoutsDialog open={running} onOpenChange={setRunning} />
    </div>
  );
}
